import React from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadCloud, FileText, X, Download, RefreshCw, Shield, Clock, Trash2 } from 'lucide-react';
import { api, formatApiErrorDetail, formatBytes } from '../lib/api';
import { toast } from 'sonner';

const STATES = { IDLE: 'idle', UPLOADING: 'uploading', PROCESSING: 'processing', DONE: 'done', ERROR: 'error' };

export default function ToolUploader({ tool, extraFields = null, getExtraFormData = null }) {
  const [files, setFiles] = React.useState([]);
  const [state, setState] = React.useState(STATES.IDLE);
  const [progress, setProgress] = React.useState(0);
  const [resultUrl, setResultUrl] = React.useState(null);
  const [resultName, setResultName] = React.useState('');
  const [resultSize, setResultSize] = React.useState(0);
  const [error, setError] = React.useState('');

  const onDrop = React.useCallback(
    (accepted) => {
      if (!accepted.length) return;
      if (tool.multiple) setFiles((prev) => [...prev, ...accepted]);
      else setFiles(accepted.slice(0, 1));
    },
    [tool.multiple]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: tool.multiple,
    noClick: true,
    accept: undefined,
  });

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const reset = () => {
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setFiles([]); setState(STATES.IDLE); setProgress(0);
    setResultUrl(null); setResultName(''); setResultSize(0); setError('');
  };

  const process = async () => {
    if (!files.length) return;
    setState(STATES.UPLOADING); setProgress(0); setError('');

    const formData = new FormData();
    if (tool.multiple) files.forEach((f) => formData.append('files', f));
    else formData.append('file', files[0]);

    if (getExtraFormData) {
      const extras = getExtraFormData();
      if (extras && extras.__error) {
        setError(extras.__error);
        setState(STATES.ERROR);
        toast.error(extras.__error);
        return;
      }
      if (extras) Object.entries(extras).forEach(([k, v]) => formData.append(k, v));
    }

    try {
      const response = await api.post(`/tools/${tool.slug}`, formData, {
        responseType: 'blob',
        onUploadProgress: (e) => {
          if (e.total) {
            const p = Math.round((e.loaded * 60) / e.total);
            setProgress(p);
            if (p >= 60) setState(STATES.PROCESSING);
          }
        },
      });

      // Animate progress to 100
      let p = 60;
      const tick = setInterval(() => {
        p = Math.min(99, p + 3);
        setProgress(p);
      }, 80);

      const blob = response.data;
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const name = match ? match[1] : `result.${tool.slug.includes('jpg') || tool.slug.includes('split') ? 'zip' : 'pdf'}`;
      const url = URL.createObjectURL(blob);
      clearInterval(tick);
      setProgress(100);
      setResultUrl(url);
      setResultName(name);
      setResultSize(blob.size);
      setState(STATES.DONE);
      toast.success('Done! Your file is ready to download.');
    } catch (err) {
      // err.response.data may be a Blob containing JSON error
      let detail = 'Processing failed';
      if (err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail || detail; } catch {}
      } else {
        detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      }
      setError(detail);
      setState(STATES.ERROR);
      toast.error(detail);
    }
  };

  return (
    <div data-testid="tool-uploader" className="w-full">
      {state === STATES.IDLE && (
        <div className="space-y-6">
          <div
            {...getRootProps()}
            data-testid="dropzone"
            className={`dropzone rounded-2xl p-10 md:p-16 text-center cursor-pointer ${isDragActive ? 'active' : ''}`}
            onClick={open}
          >
            <input {...getInputProps()} data-testid="file-input" />
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-vault-gold/10 flex items-center justify-center border border-vault-gold/30">
                  <UploadCloud className="w-9 h-9 text-vault-gold" />
                </div>
                <div className="absolute inset-0 rounded-full animate-pulse-glow" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-heading font-semibold text-white">
                  Drag &amp; drop your {tool.multiple ? 'files' : 'file'} here
                </h3>
                <p className="text-gray-400 text-sm mt-1">or click to browse — {tool.accept}</p>
              </div>
              <button
                data-testid="select-files-button"
                className="btn-primary mt-2"
                onClick={(e) => { e.stopPropagation(); open(); }}
              >
                Select {tool.multiple ? 'Files' : 'File'}
              </button>
              <p className="text-xs text-gray-500 font-mono tracking-wider mt-2 flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> AUTO-DELETE IN 60 MINUTES
              </p>
            </div>
          </div>

          {files.length > 0 && (
            <div data-testid="file-list" className="space-y-2">
              <p className="text-sm text-gray-400 font-mono tracking-wider uppercase">
                {files.length} file{files.length > 1 ? 's' : ''} selected
              </p>
              {files.map((f, i) => (
                <div
                  key={i}
                  data-testid={`file-item-${i}`}
                  className="flex items-center justify-between bg-vault-paper/60 border border-white/10 rounded-lg p-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-vault-gold shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{f.name}</p>
                      <p className="text-xs text-gray-500">{formatBytes(f.size)}</p>
                    </div>
                  </div>
                  <button
                    data-testid={`remove-file-${i}`}
                    onClick={() => removeFile(i)}
                    className="text-gray-500 hover:text-red-400 transition shrink-0 ml-3"
                    aria-label="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {extraFields && (
                <div className="mt-6 p-5 bg-vault-paper/40 border border-white/10 rounded-xl">
                  {extraFields}
                </div>
              )}

              <button
                data-testid="process-button"
                onClick={process}
                disabled={!files.length}
                className="btn-primary w-full mt-4 text-base"
              >
                {tool.name} Now →
              </button>
            </div>
          )}
        </div>
      )}

      {(state === STATES.UPLOADING || state === STATES.PROCESSING) && (
        <div data-testid="processing-view" className="text-center py-16">
          <div className="w-20 h-20 rounded-full bg-vault-gold/10 border border-vault-gold/30 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-vault-gold animate-pulse" />
          </div>
          <h3 className="text-2xl font-heading font-semibold text-white mb-2">
            {state === STATES.UPLOADING ? 'Securing your file…' : 'Processing in the vault…'}
          </h3>
          <p className="text-gray-400 text-sm mb-8 font-mono tracking-wider">{progress}% COMPLETE</p>
          <div className="max-w-xl mx-auto bg-vault-paper/60 rounded-full h-2 overflow-hidden border border-white/10">
            <div
              data-testid="progress-bar"
              className="h-full bg-gradient-to-r from-vault-gold to-vault-gold-hover transition-all duration-300 shimmer"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {state === STATES.DONE && resultUrl && (
        <div data-testid="result-view" className="text-center py-12">
          <div className="w-20 h-20 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-green-400" />
          </div>
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-2">Vault Sealed.</h3>
          <p className="text-gray-400 mb-1">Your file is ready — {formatBytes(resultSize)}</p>
          <p className="text-xs text-gray-500 font-mono tracking-wider mb-8 flex items-center gap-1.5 justify-center">
            <Trash2 className="w-3 h-3" /> WILL BE DELETED IN 60 MINUTES
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              data-testid="download-button"
              href={resultUrl}
              download={resultName}
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              <Download className="w-5 h-5" /> Download {resultName}
            </a>
            <button data-testid="process-another-button" onClick={reset} className="btn-ghost inline-flex items-center justify-center gap-2">
              <RefreshCw className="w-5 h-5" /> Process Another
            </button>
          </div>
        </div>
      )}

      {state === STATES.ERROR && (
        <div data-testid="error-view" className="text-center py-12">
          <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-red-400" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-white mb-2">Vault rejected the file.</h3>
          <p data-testid="error-message" className="text-red-400 mb-6 max-w-md mx-auto">{error}</p>
          <button data-testid="try-again-button" onClick={reset} className="btn-ghost">Try again</button>
        </div>
      )}
    </div>
  );
}
